import React, { useState, useEffect } from 'react';
import { collection, updateDoc, doc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface FineManagementProps {
  domain: string;
}

interface Fine {
  id: string;
  circulationId: string;
  bookTitle: string;
  memberId: string;
  memberName: string;
  memberType: string;
  fineAmount: number;
  paid: boolean;
  paidOn: string | null;
  paidMethod: string;
  reason: string;
  issuedOn: string;
  dueDate: string;
  returnDate: string;
  createdAt: string;
}

const FineManagement: React.FC<FineManagementProps> = ({ domain }) => {
  const [fines, setFines] = useState<Fine[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('unpaid');

  useEffect(() => {
    if (!domain) return;

    const finesRef = collection(db, `school-data/${domain}/library-fines`);
    const q = query(finesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list: Fine[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          circulationId: data.circulationId || '',
          bookTitle: data.bookTitle || '',
          memberId: data.memberId || '',
          memberName: data.memberName || '',
          memberType: data.memberType || '',
          fineAmount: data.fineAmount || 0,
          paid: data.paid || false,
          paidOn: data.paidOn || null,
          paidMethod: data.paidMethod || '',
          reason: data.reason || 'Late return',
          issuedOn: data.issuedOn || '',
          dueDate: data.dueDate || '',
          returnDate: data.returnDate || '',
          createdAt: data.createdAt || '',
        });
      });
      setFines(list);
      setLoading(false);
    }, (error) => {
      console.error('Error loading fines:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [domain]);

  const markAsPaid = async (fine: Fine, method: string) => {
    try {
      const fineRef = doc(db, `school-data/${domain}/library-fines`, fine.id);
      await updateDoc(fineRef, {
        paid: true,
        paidOn: new Date().toISOString().split('T')[0],
        paidMethod: method,
      });

      // Also update the circulation record
      if (fine.circulationId) {
        const circRef = doc(db, `school-data/${domain}/library-circulation`, fine.circulationId);
        await updateDoc(circRef, { finePaid: true });
      }
    } catch (error: any) {
      console.error('Error marking fine as paid:', error);
      alert(`Failed to update: ${error.message}`);
    }
  };

  const waiveFine = async (fine: Fine) => {
    if (!confirm(`Waive fine of Rs ${fine.fineAmount} for ${fine.memberName}?`)) return;
    try {
      const fineRef = doc(db, `school-data/${domain}/library-fines`, fine.id);
      await updateDoc(fineRef, {
        paid: true,
        paidOn: new Date().toISOString().split('T')[0],
        paidMethod: 'Waived',
      });

      if (fine.circulationId) {
        const circRef = doc(db, `school-data/${domain}/library-circulation`, fine.circulationId);
        await updateDoc(circRef, { finePaid: true });
      }
    } catch (error: any) {
      alert(`Failed to waive fine: ${error.message}`);
    }
  };

  const filtered = fines.filter((f) => {
    const matchesSearch = !search ||
      f.bookTitle.toLowerCase().includes(search.toLowerCase()) ||
      f.memberName.toLowerCase().includes(search.toLowerCase()) ||
      f.memberId.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'unpaid' && !f.paid) ||
      (statusFilter === 'paid' && f.paid);
    return matchesSearch && matchesStatus;
  });

  const totalFines = fines.reduce((sum, f) => sum + f.fineAmount, 0);
  const totalCollected = fines.filter((f) => f.paid && f.paidMethod !== 'Waived').reduce((sum, f) => sum + f.fineAmount, 0);
  const totalPending = fines.filter((f) => !f.paid).reduce((sum, f) => sum + f.fineAmount, 0);
  const totalWaived = fines.filter((f) => f.paid && f.paidMethod === 'Waived').reduce((sum, f) => sum + f.fineAmount, 0);

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-2 text-muted">Loading fines data...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-cash-stack fs-3 text-primary"></i>
              <h4 className="mb-0 mt-1">Rs {totalFines}</h4>
              <small className="text-muted">Total Fines</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-check-circle fs-3 text-success"></i>
              <h4 className="mb-0 mt-1">Rs {totalCollected}</h4>
              <small className="text-muted">Collected</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-exclamation-circle fs-3 text-danger"></i>
              <h4 className="mb-0 mt-1">Rs {totalPending}</h4>
              <small className="text-muted">Pending</small>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="card border-0 shadow-sm text-center">
            <div className="card-body">
              <i className="bi bi-slash-circle fs-3 text-secondary"></i>
              <h4 className="mb-0 mt-1">Rs {totalWaived}</h4>
              <small className="text-muted">Waived</small>
            </div>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white py-3">
          <h5 className="mb-0">
            <i className="bi bi-cash-coin me-2 text-primary-custom"></i>
            Fines & Dues
          </h5>
        </div>
        <div className="card-body">
          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by book, member name, or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="unpaid">Unpaid</option>
                <option value="paid">Paid</option>
                <option value="all">All Fines</option>
              </select>
            </div>
            <div className="col-md-3 text-end">
              <span className="text-muted small">{filtered.length} records</span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox fs-1 d-block mb-2"></i>
              {fines.length === 0 ? 'No fines recorded yet.' : 'No fines match your filter.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Member</th>
                    <th>Book</th>
                    <th>Due Date</th>
                    <th>Returned</th>
                    <th>Reason</th>
                    <th className="text-end">Fine Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((fine) => (
                    <tr key={fine.id}>
                      <td>
                        <strong>{fine.memberName}</strong>
                        <small className="text-muted d-block">{fine.memberId} ({fine.memberType})</small>
                      </td>
                      <td>{fine.bookTitle}</td>
                      <td><small>{fine.dueDate}</small></td>
                      <td><small>{fine.returnDate}</small></td>
                      <td><small>{fine.reason}</small></td>
                      <td className="text-end fw-bold">Rs {fine.fineAmount}</td>
                      <td>
                        {fine.paid ? (
                          <span className={`badge ${fine.paidMethod === 'Waived' ? 'bg-secondary' : 'bg-success'}`}>
                            {fine.paidMethod === 'Waived' ? 'Waived' : 'Paid'}
                          </span>
                        ) : (
                          <span className="badge bg-danger">Unpaid</span>
                        )}
                        {fine.paidOn && (
                          <small className="text-muted d-block">{fine.paidOn}</small>
                        )}
                      </td>
                      <td>
                        {!fine.paid && (
                          <div className="d-flex gap-1">
                            <div className="dropdown">
                              <button className="btn btn-sm btn-outline-success dropdown-toggle" data-bs-toggle="dropdown">
                                <i className="bi bi-check me-1"></i>Collect
                              </button>
                              <ul className="dropdown-menu">
                                <li><button className="dropdown-item" onClick={() => markAsPaid(fine, 'Cash')}>Cash</button></li>
                                <li><button className="dropdown-item" onClick={() => markAsPaid(fine, 'UPI')}>UPI</button></li>
                                <li><button className="dropdown-item" onClick={() => markAsPaid(fine, 'Card')}>Card</button></li>
                              </ul>
                            </div>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => waiveFine(fine)} title="Waive Fine">
                              <i className="bi bi-slash-circle"></i>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FineManagement;
